import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getQuoteV2Query } from 'Services/Query/QuotesV2';
import type { QuoteV2 } from 'Services/Query/QuotesV2/Types';
import { getCustomerQuery } from '@setvi/shared/services/react-query';

/**
 * Fetch quote details by ID (without items)
 * Uses the v2 endpoint: GET /v2/quote/:quoteId
 * Items are fetched separately via useQuoteItems
 *
 * When quote has an account._id, fetches full customer data
 * and merges it into quote.account for downstream use (catalog, pricing)
 */
export const useQuote = (quoteId: string) => {
  const query = useQuery<QuoteV2>({
    ...getQuoteV2Query(quoteId),
    enabled: !!quoteId
  });

  const accountId = query.data?.account?._id;

  const { data: customer } = useQuery({
    ...getCustomerQuery({ customerId: accountId }),
    enabled: !!accountId
  });

  const data = useMemo(() => {
    if (!query.data) return query.data;

    if (customer) {
      return {
        ...query.data,
        account: {
          ...query.data.account,
          ...customer
        }
      };
    }

    return { ...query.data, account: null };
  }, [query.data, customer]);

  return {
    ...query,
    data
  };
};
